import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { type RootState } from '~redux/rootReducer'

export const SESSION_WARNING_MS = 5 * 60 * 1000

export type SessionExpiryStatus = 'active' | 'warning' | 'expired'

export interface SessionExpiryState {
  status: SessionExpiryStatus
  remainingMs: number
}

const TICK_MS = 1000

function computeDeadline(sessionExpiresAt: string | null): number {
  if (!sessionExpiresAt) return Infinity
  const ttlDeadline = Date.parse(sessionExpiresAt)
  return Number.isNaN(ttlDeadline) ? Infinity : ttlDeadline
}

export function useSessionExpiry(): SessionExpiryState {
  const isAuthenticated = useSelector((state: RootState) => state?.auth.isAuthenticated)
  const sessionExpiresAt = useSelector((state: RootState) => state?.auth.sessionExpiresAt ?? null)

  const hasTimeout = Boolean(sessionExpiresAt)

  const [remainingMs, setRemainingMs] = useState<number>(() => {
    if (!isAuthenticated || !hasTimeout) return Infinity
    return computeDeadline(sessionExpiresAt) - Date.now()
  })

  useEffect(() => {
    if (!isAuthenticated || !hasTimeout) {
      setRemainingMs(Infinity)
      return
    }

    const update = (): void => {
      setRemainingMs(Math.max(0, computeDeadline(sessionExpiresAt) - Date.now()))
    }

    update()
    const interval = window.setInterval(update, TICK_MS)
    return (): void => window.clearInterval(interval)
  }, [isAuthenticated, hasTimeout, sessionExpiresAt])

  let status: SessionExpiryStatus = 'active'
  if (isAuthenticated && hasTimeout) {
    if (remainingMs <= 0) {
      status = 'expired'
    } else if (remainingMs <= SESSION_WARNING_MS) {
      status = 'warning'
    }
  }

  return { status, remainingMs }
}
